<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute } from 'vue-router';
import { emitter } from 'shared/helpers/mitt';
import whatsappCallAPI from 'dashboard/api/whatsappCall';

// ============================================================================
// v109 (WhatsApp Call — Fase 3): POPUP de chamada recebida — telefone
// PISCANDO + número/nome de quem chama + RECUSAR (vermelho) / ATENDER (verde).
// Aparece em QUALQUER tela. O evento chega pelo ActionCable
// ('whatsapp_call.incoming' → emitter 'whatsappCall:incoming') e o backend
// SÓ envia pra usuários com o toggle ativo e com a CHAVE GERAL ligada —
// então o widget não precisa checar flags.
// ⚠️ Áudio (SIP.js ↔ PBX 167) entra na Fase 2c/homologação: até lá, Atender
// registra o aceite e abre a conversa do cliente.
// ============================================================================

const route = useRoute();
const chamada = ref(null); // { id, phone_number, contato?, conversation_id }
const emAtendimento = ref(false);
const segundos = ref(0);
let timer = null;
let toqueTimer = null;

const accountId = computed(() => route.params.accountId);

const aoReceber = data => {
  if (emAtendimento.value || chamada.value) return; // já tratando uma
  chamada.value = data;
  segundos.value = 0;
};

const limpar = () => {
  clearInterval(timer);
  timer = null;
  chamada.value = null;
  emAtendimento.value = false;
  segundos.value = 0;
};

const atender = async () => {
  try {
    await whatsappCallAPI.atender(accountId.value, chamada.value.id);
    emAtendimento.value = true;
    timer = setInterval(() => {
      segundos.value += 1;
    }, 1000);
    // abre a conversa do cliente, se existir
    if (chamada.value.conversation_id) {
      window.location.hash = '';
      const url = `/app/accounts/${accountId.value}/conversations/${chamada.value.conversation_id}`;
      if (!window.location.pathname.includes(`/conversations/${chamada.value.conversation_id}`)) {
        window.history.pushState({}, '', url);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }
  } catch (e) {
    // chamada pode já ter caído
    limpar();
  }
};

const recusar = async () => {
  try {
    await whatsappCallAPI.recusar(accountId.value, chamada.value.id);
  } finally {
    limpar();
  }
};

const encerrar = () => limpar();

const fmt = s => {
  const m = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
};

const aoAtualizar = data => {
  // chamada terminou/foi atendida em outro lugar → fecha o popup
  if (chamada.value && data?.id === chamada.value.id && ['ended', 'missed', 'rejected', 'failed'].includes(data.status)) {
    limpar();
  }
};

onMounted(() => {
  emitter.on('whatsappCall:incoming', aoReceber);
  emitter.on('whatsappCall:update', aoAtualizar);
});
onBeforeUnmount(() => {
  emitter.off('whatsappCall:incoming', aoReceber);
  emitter.off('whatsappCall:update', aoAtualizar);
  clearInterval(timer);
  clearInterval(toqueTimer);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <!-- ============ TOCANDO ============ -->
  <div v-if="chamada && !emAtendimento" class="wcall-popup">
    <div class="wcall-anel">
      <span class="wcall-fone pisca"><span class="i-lucide-phone-incoming size-7" /></span>
    </div>
    <p class="wcall-titulo">Ligação do WhatsApp</p>
    <p class="wcall-numero tabular-nums">{{ chamada.contato || chamada.phone_number || 'Número desconhecido' }}</p>
    <p v-if="chamada.contato && chamada.phone_number" class="wcall-sub tabular-nums">{{ chamada.phone_number }}</p>
    <div class="wcall-acoes">
      <button type="button" class="wcall-btn recusar" @click="recusar">
        <span class="i-lucide-phone-off size-5" />
        Recusar
      </button>
      <button type="button" class="wcall-btn atender" @click="atender">
        <span class="i-lucide-phone size-5" />
        Atender
      </button>
    </div>
  </div>

  <!-- ============ EM ATENDIMENTO ============ -->
  <div v-else-if="chamada && emAtendimento" class="wcall-popup ativa">
    <div class="flex gap-3 items-center">
      <span class="wcall-fone ativo"><span class="i-lucide-phone-call size-5" /></span>
      <div class="flex-1 min-w-0">
        <p class="wcall-titulo-min truncate">{{ chamada.contato || chamada.phone_number }}</p>
        <p class="wcall-timer tabular-nums">{{ fmt(segundos) }}</p>
      </div>
      <button type="button" class="wcall-btn recusar compacto" @click="encerrar">
        <span class="i-lucide-phone-off size-4" />
      </button>
    </div>
  </div>
</template>

<style scoped>
/* v109 — popup de ligação (tema escuro, glow verde WhatsApp) */
.wcall-popup {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 99990;
  width: 320px;
  padding: 22px 20px 20px;
  border-radius: 18px;
  background: linear-gradient(180deg, #17181d, #101115);
  border: 2px solid #25d366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.2), 0 18px 48px rgba(0, 0, 0, 0.55);
  color: #fff;
  text-align: center;
  animation: wcall-entra 0.25s ease;
}
.wcall-popup.ativa {
  width: 300px;
  padding: 14px 16px;
  text-align: left;
}
@keyframes wcall-entra {
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
.wcall-anel {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}
.wcall-fone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background: rgba(37, 211, 102, 0.16);
  color: #25d366;
}
.wcall-fone.pisca {
  animation: wcall-pisca 1s ease-in-out infinite;
}
@keyframes wcall-pisca {
  0%, 100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.55); transform: scale(1); }
  50% { box-shadow: 0 0 0 16px rgba(37, 211, 102, 0); transform: scale(1.08); }
}
.wcall-fone.ativo {
  width: 40px;
  height: 40px;
}
.wcall-titulo {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
}
.wcall-numero {
  margin-top: 2px;
  font-size: 18px;
  font-weight: 800;
}
.wcall-sub {
  font-size: 12px;
  color: #9ca3af;
}
.wcall-acoes {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}
.wcall-btn {
  flex: 1;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 11px 0;
  border: none;
  border-radius: 12px;
  font-weight: 800;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  transition: transform 0.1s ease;
}
.wcall-btn:hover { transform: translateY(-1px); }
.wcall-btn.recusar { background: #e5484d; }
.wcall-btn.atender { background: #25d366; color: #06301a; }
.wcall-btn.compacto { flex: 0 0 40px; padding: 10px 0; border-radius: 10px; }
.wcall-titulo-min { font-size: 14px; font-weight: 700; }
.wcall-timer { font-size: 12px; color: #25d366; font-weight: 700; }
</style>
